// useCascadePredictor — single-source hook that owns the Cascade Predictor
// lifecycle (3 trigger effects, score computation, AI fetch, localStorage
// hydration/persistence) for the Liquidity Map.
//
// 2026-02 FASE 4 refactor. Extracted from LiqMapPage.jsx (lines 60-92, 367-650
// of the previous version). Same external behaviour as before, with TWO new
// bug fixes baked in:
//
//   FIX A — Coin-switch race condition. Every fetch now carries an
//   AbortController whose signal is aborted on coin change (or component
//   unmount). A previously in-flight predictor call for an old coin can no
//   longer resolve and inject stale data into the new coin's state.
//
//   FIX B — Double-entry window. `isPredictorRunning` is now flipped to true
//   IMMEDIATELY after the already-running guard, BEFORE any other early-
//   return paths (snapshot-stale retry, missing inputs, score-below-
//   threshold cache hit, 5-min cache hit). The entire body is wrapped in a
//   try/finally so EVERY exit path resets the flag.
//
// Also cleaned up:
//   - Removed dead `_evaluated_at` pin (line 473-482 of the previous version
//     was written but never read for render; verified by grep in the audit).
//   - Consolidated the duplicated localStorage hydration logic (previously
//     in two places: lines 601-613 + 633-650 of the old file) into a single
//     `_hydrateFromCache` helper.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API } from "../lib/api";

// ─── Public constants (consumed by the banner gating logic) ────────────────
export const CASCADE_SHOW_THRESHOLD = 20;
export const CASCADE_STICKY_MS = 5 * 60 * 1000;    // 5 min sticky banner
export const CASCADE_LS_TTL_MS = 10 * 60 * 1000;   // 10 min localStorage freshness
const PREDICTOR_CACHE_MS = 5 * 60 * 1000;          // 5-min cache between fetches
const POLL_INTERVAL_MS = 60_000;
const PRICE_MOVE_TRIGGER_PCT = 0.5;
const SNAPSHOT_RETRY_MS = 800;
const SNAPSHOT_RETRY_MAX = 3;

// ─── Pure helpers (also used by the banner render for `tipo` fallback) ─────
export const computeScore = (precio, clusterMax, intensidad, liqLongs, liqShorts, cambio24h) => {
  if (!precio || !clusterMax) return 0;
  let score = 0;
  const distPct = (Math.abs(precio - clusterMax) / precio) * 100;
  if (distPct < 1) score += 40;
  else if (distPct < 2) score += 25;
  else if (distPct < 3) score += 15;
  else if (distPct < 5) score += 5;
  if (intensidad > 0.85) score += 25;
  else if (intensidad > 0.65) score += 15;
  else if (intensidad > 0.45) score += 8;
  const ratio = liqLongs / (liqShorts || 1);
  if (ratio > 2.0) score += 10;
  if (ratio < 0.5) score += 10;
  if (Math.abs(cambio24h || 0) > 5) score += 5;
  return Math.min(score, 100);
};

export const cascadeTipo = (precio, clusterMax) =>
  (precio > clusterMax ? "LONG CASCADE" : "SHORT CASCADE");

// ─── Hook ──────────────────────────────────────────────────────────────────
export const useCascadePredictor = ({
  snapshot,
  livePrice,
  coin,
  token,
  hasAccess,
  cluster,         // output of useClusterData(snapshot)
  change24,        // statsForBar.change24
}) => {
  // State
  const [cascadeScore, setCascadeScore] = useState(0);
  const [cascadeData, setCascadeData] = useState(null);
  const [cascadeLoading, setCascadeLoading] = useState(false);
  const [bannerClosed, setBannerClosed] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Refs
  const lastPredictorCall = useRef({});
  const lastEvaluatedPrice = useRef(0);
  const lastValidData = useRef(null);
  const lastValidScore = useRef(0);
  const isPredictorRunning = useRef(false);
  const bannerLastShownAt = useRef(null);

  // FIX A — controller of the current in-flight fetch (if any).
  const currentControllerRef = useRef(null);

  // Latest-value refs — let the callback read fresh inputs without listing
  // them as deps (which would invalidate callPredictor on every tick).
  const latest = useRef({
    snapshot, livePrice, coin, token, cluster, change24,
  });
  useEffect(() => {
    latest.current = { snapshot, livePrice, coin, token, cluster, change24 };
  }, [snapshot, livePrice, coin, token, cluster, change24]);

  // Ref of cascadeData — read inside callPredictor for the 5-min cache check
  // without making it a callback dep.
  const cascadeDataRef = useRef(cascadeData);
  useEffect(() => { cascadeDataRef.current = cascadeData; }, [cascadeData]);

  // ─── Consolidated localStorage hydration helper (used on mount + on
  // every coin change). Returns true when state was hydrated.
  const _hydrateFromCache = useCallback((targetCoin) => {
    try {
      const saved = localStorage.getItem(`rebel_cascade_last_${targetCoin}`);
      if (!saved) return false;
      const parsed = JSON.parse(saved);
      if (!parsed || Date.now() - parsed.timestamp >= CASCADE_LS_TTL_MS) return false;
      setCascadeData(parsed.resultado);
      setCascadeScore(parsed.score);
      lastValidData.current = parsed.resultado;
      lastValidScore.current = parsed.score;
      bannerLastShownAt.current = parsed.timestamp;
      return true;
    } catch {
      return false;
    }
  }, []);

  // ─── callPredictor (with FIX A + FIX B) ──────────────────────────────────
  const callPredictor = useCallback(async () => {
    // FIX B — overlap guard FIRST, flag flipped IMMEDIATELY after the check
    // (before any other early return path), then wrapped in try/finally so
    // every exit resets the flag.
    if (isPredictorRunning.current) return;
    isPredictorRunning.current = true;

    let _retryScheduled = false;  // separate from the flag — controls whether
                                  // finally should keep the flag true (handled
                                  // explicitly inside the try block).
    const controller = new AbortController();
    currentControllerRef.current = controller;

    try {
      const {
        snapshot: snap, livePrice: lp, coin: curCoin, token: tok,
        cluster: clu, change24: ch24,
      } = latest.current;

      // ─ Snapshot-stale retry (preserves the original 3×800ms chain) ─
      if (!snap || snap.coin !== curCoin) {
        const tries = callPredictor._retries || 0;
        if (tries < SNAPSHOT_RETRY_MAX) {
          callPredictor._retries = tries + 1;
          _retryScheduled = true;
          setTimeout(() => {
            callPredictor._retries = 0;
            // The flag was reset in finally below before this setTimeout fires.
            callPredictor();
          }, SNAPSHOT_RETRY_MS);
        } else {
          callPredictor._retries = 0;
        }
        return;
      }
      callPredictor._retries = 0;

      const s = snap?.stats || {};
      const precio = lp ?? s.price_avg;
      const clusterMax = clu?.maxPrice;
      if (!precio || !clusterMax || !tok) return;

      const intensidad = clu?.maxIntensity ?? 0.7;
      const liqLongs = Number(s.liq_longs_24h) || 0;
      const liqShorts = Number(s.liq_shorts_24h) || 0;
      const cambio24h = ch24 || 0;
      const score = computeScore(precio, clusterMax, intensidad, liqLongs, liqShorts, cambio24h);

      const ahora = Date.now();
      const ultima = lastPredictorCall.current[curCoin] || 0;
      const cacheVigente = ahora - ultima < PREDICTOR_CACHE_MS;

      // Threshold gate (FIX 1 / CAUSE A from the audit — score≤20 holds the
      // last valid analysis while cache is fresh, otherwise clears).
      if (score <= CASCADE_SHOW_THRESHOLD) {
        if (cacheVigente && lastValidData.current) {
          setCascadeScore(lastValidScore.current);
          return;
        }
        setCascadeScore(score);
        setCascadeData(null);
        lastValidData.current = null;
        lastValidScore.current = 0;
        return;
      }

      setCascadeScore(score);
      if (cacheVigente && cascadeDataRef.current) return; // honor 5-min cache

      const distancia_pct = (Math.abs(precio - clusterMax) / precio) * 100;
      const tipo = cascadeTipo(precio, clusterMax);
      setCascadeLoading(true);
      setBannerClosed(false);

      const r = await fetch(`${API}/liqmap/predictor/${curCoin}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({
          precio_actual: precio,
          cluster_max: clusterMax,
          cluster_sup: clu?.supPrice,
          intensidad_max: intensidad,
          liq_longs_24h: liqLongs,
          liq_shorts_24h: liqShorts,
          open_interest: Number(s.open_interest_total_usd) || 0,
          cambio_24h: cambio24h,
          num_exchanges: (snap?.sources || snap?.stats?.sources || []).length,
          score,
          tipo,
          distancia_pct,
          idioma: "español",
        }),
        signal: controller.signal,
      });
      const data = await r.json();

      // FIX A — defensive: if the fetch resolves but `coin` changed mid-flight
      // (race window between abort signal and Promise resolution), discard.
      if (controller.signal.aborted || latest.current.coin !== curCoin) return;

      setCascadeData(data);
      lastPredictorCall.current[curCoin] = ahora;

      if (data && typeof data.score === "number" && data.score > CASCADE_SHOW_THRESHOLD) {
        lastValidData.current = data;
        lastValidScore.current = data.score;
        bannerLastShownAt.current = ahora;
        // Persist last good result so a route-change / reload doesn't blank
        // the banner inside the 10-min freshness window.
        try {
          localStorage.setItem(`rebel_cascade_last_${curCoin}`, JSON.stringify({
            resultado: data,
            score: data.score,
            timestamp: ahora,
            precio_evaluado: precio,
          }));
        } catch { /* localStorage opcional */ }
      }
      // History log
      try {
        const key = `rebel_cascade_history_${curCoin}`;
        const arr = JSON.parse(localStorage.getItem(key) || "[]");
        arr.unshift({
          timestamp: ahora, coin: curCoin, score: data.score, precio,
          cluster_max: clusterMax, tipo: data.tipo,
          analisis: data.analisis, nivel: data.nivel,
        });
        if (arr.length > 10) arr.length = 10;
        localStorage.setItem(key, JSON.stringify(arr));
      } catch { /* localStorage opcional */ }
    } catch (e) {
      // FIX A — distinguish intentional abort from real network error.
      if (e && (e.name === "AbortError" || e.code === 20)) {
        // silent: the coin changed mid-flight, nothing to recover from.
        return;
      }
      console.warn("cascade predictor err", e);
    } finally {
      setCascadeLoading(false);
      isPredictorRunning.current = false;
      if (currentControllerRef.current === controller) {
        currentControllerRef.current = null;
      }
      // `_retryScheduled` is informational — the setTimeout above runs after
      // this finally completes so the flag is correctly cleared before retry.
      void _retryScheduled;
    }
  }, []);

  // We need a ref of cascadeData for the 5-min cache check inside callPredictor
  // without making it a dep (which would re-create the callback on every fetch).
  // Declared earlier in the hook body — this comment is a tombstone.

  // ─── Effect A: 60s polling ──────────────────────────────────────────────
  useEffect(() => {
    if (!hasAccess) return undefined;
    callPredictor();
    const id = setInterval(callPredictor, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [coin, hasAccess, callPredictor]);

  // ─── Effect B: cluster_max first availability ───────────────────────────
  useEffect(() => {
    if (!hasAccess) return;
    if (!cluster?.maxPrice) return;
    callPredictor();
  }, [cluster?.maxPrice, hasAccess, callPredictor]);

  // ─── Effect C: price tick (>0.5% move) ──────────────────────────────────
  useEffect(() => {
    if (!hasAccess) return;
    if (!livePrice) return;
    const last = lastEvaluatedPrice.current;
    if (last > 0) {
      const cambio = (Math.abs(livePrice - last) / last) * 100;
      if (cambio < PRICE_MOVE_TRIGGER_PCT) return;
    }
    lastEvaluatedPrice.current = livePrice;
    callPredictor();
  }, [livePrice, hasAccess, callPredictor]);

  // ─── Coin-change reset + localStorage hydration ─────────────────────────
  // FIX A — abort any in-flight fetch for the previous coin BEFORE clearing
  // state so a late-resolving response cannot re-inject data into the new
  // coin's slot.
  const firstMountRef = useRef(true);
  useEffect(() => {
    // Abort previous in-flight fetch (FIX A)
    if (currentControllerRef.current) {
      try { currentControllerRef.current.abort(); } catch { /* noop */ }
      currentControllerRef.current = null;
    }
    // Reset state + refs
    if (!firstMountRef.current) {
      setCascadeData(null);
      setCascadeScore(0);
      setBannerClosed(false);
      setShowHistory(false);
    }
    firstMountRef.current = false;
    lastPredictorCall.current = {};
    lastEvaluatedPrice.current = 0;
    lastValidData.current = null;
    lastValidScore.current = 0;
    isPredictorRunning.current = false;
    bannerLastShownAt.current = null;
    // Hydrate from localStorage if fresh — replaces both the coin-change
    // hydration (old lines 601-613) and the mount-time hydration (old
    // lines 633-650) of LiqMapPage.jsx.
    _hydrateFromCache(coin);
  }, [coin, _hydrateFromCache]);

  // ─── "Actualizado hace Xs" 1s ticker ────────────────────────────────────
  useEffect(() => {
    if (!cascadeData) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cascadeData]);

  // ─── Cleanup on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (currentControllerRef.current) {
        try { currentControllerRef.current.abort(); } catch { /* noop */ }
      }
    };
  }, []);

  // Derived for the banner gating
  const stickyOk = useMemo(() => {
    return !!bannerLastShownAt.current
      && (now - bannerLastShownAt.current < CASCADE_STICKY_MS);
  }, [now, cascadeData, cascadeScore]);  // re-evaluate when state ticks

  const lastCallAt = lastPredictorCall.current[coin];

  return {
    cascadeScore,
    cascadeData,
    cascadeLoading,
    bannerClosed, setBannerClosed,
    showHistory, setShowHistory,
    now,
    stickyOk,
    lastCallAt,
    CASCADE_SHOW_THRESHOLD,
    CASCADE_STICKY_MS,
  };
};

export default useCascadePredictor;
